import { get, map } from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";

import type {
  ClientConfig,
  GlobalConfig,
  WebhookHeader,
} from "@platform/dmv-api";

import { updateClientsConfig } from "../../lib/automatic";
import ButtonPrimary from "../ButtonPrimary";
import SettingsSection from "./SettingsSection";
import WebhookHeaderModal from "./WebhookHeaderModal";
import WebhookUrlModal from "./WebhookUrlModal";

const WebhooksSection = ({
  id,
  config,
  clientId,
}: {
  id?: string;
  config: ClientConfig | null;
  clientId: string;
}) => {
  const [webhookUris, setWebhookUris] = useState<string[]>(
    normalizeWebhookUris(get(config, "globalConfig.webhookUri"))
  );
  const [webhookHeaders, setWebhookHeaders] = useState<WebhookHeader[]>(
    get(config, "globalConfig.webhookHeaders", [])
  );

  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [editingUrlIndex, setEditingUrlIndex] = useState<number | null>(null);

  const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false);

  // Existing headers keep their stored secrets if we send `key` only. Sending
  // a `value` for a key that already exists is rejected ("cannot edit an
  // existing entry"). New headers include both `key` and `value`.
  const saveWebhooksToApi = async (
    globalConfig: {
      webhookUri?: GlobalConfig["webhookUri"];
      webhookHeaders?: Array<{ key: string; value?: string }>;
    },
    successMessage: string
  ) => {
    const loadingToast = toast.loading("Saving...");
    const [, error] = await updateClientsConfig(clientId, { globalConfig });
    toast.dismiss(loadingToast);
    let errorMessageParsed = "Oops! Something went wrong. Please try again.";
    if (error) {
      switch (get(error, "code")) {
        case 400:
          const errorMessage = get(error, "message");
          if (errorMessage?.includes("duplicate")) {
            errorMessageParsed = "Duplicate webhook URL already exists.";
          }
          break
      }
      toast.error(errorMessageParsed);
      return false;
    }
    toast.success(successMessage);
    return true;
  };

  const handleOpenCreateUrlModal = () => {
    setEditingUrlIndex(null);
    setIsUrlModalOpen(true);
  };

  const handleOpenEditUrlModal = (index: number) => {
    setEditingUrlIndex(index);
    setIsUrlModalOpen(true);
  };

  const handleCloseUrlModal = () => {
    setIsUrlModalOpen(false);
    setEditingUrlIndex(null);
  };

  const handleSaveUrl = async (url: string) => {
    const isEdit = editingUrlIndex !== null;
    let updated: string[];
    if (isEdit) {
      updated = [...webhookUris];
      updated[editingUrlIndex] = url;
    } else {
      updated = [...webhookUris, url];
    }
    const success = await saveWebhooksToApi(
      { webhookUri: updated },
      isEdit ? "Webhook URL updated" : "Webhook URL added"
    );
    if (success) {
      setWebhookUris(updated);
    }
  };

  const handleSaveHeader = async (header: WebhookHeader) => {
    const payload = [...headerKeys(webhookHeaders), header];
    const success = await saveWebhooksToApi(
      { webhookHeaders: payload },
      "Webhook header added"
    );
    if (success) {
      setWebhookHeaders([...webhookHeaders, header]);
    }
  };

  const handleDeleteUrl = async (index: number) => {
    const updated = webhookUris.filter((_, i) => i !== index);
    const success = await saveWebhooksToApi(
      { webhookUri: updated },
      "Webhook URL deleted"
    );
    if (success) {
      setWebhookUris(updated);
    }
  };

  const handleDeleteHeader = async (index: number) => {
    const updated = webhookHeaders.filter((_, i) => i !== index);
    const success = await saveWebhooksToApi(
      { webhookHeaders: headerKeys(updated) },
      "Webhook header deleted"
    );
    if (success) {
      setWebhookHeaders(updated);
    }
  };

  const editingUrl =
    editingUrlIndex !== null ? webhookUris[editingUrlIndex] : null;

  return (
    <>
      <SettingsSection
        id={id}
        title="Webhooks"
        subtitle="Configure webhook endpoints to receive event notifications."
        headerAction={
          <ButtonPrimary
            text="Add Webhook"
            onClick={handleOpenCreateUrlModal}
            icon={undefined}
            className=""
          />
        }
      >
        <div className="flex flex-col gap-y-6">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Webhook URLs</h3>
            {webhookUris.length === 0 ? (
              <p className="text-sm text-gray-500">No webhook URLs configured.</p>
            ) : (
              <ul className="space-y-2">
                {webhookUris.map((uri, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between gap-x-2 p-3 bg-gray-50 rounded-md group"
                  >
                    <code className="text-sm text-gray-800 break-all">
                      {uri}
                    </code>
                    <div className="flex gap-x-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        onClick={() => handleOpenEditUrlModal(index)}
                        className="text-sm text-gray-500 hover:text-black"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUrl(index)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">
                Custom Headers
              </h3>
              <button
                onClick={() => setIsHeaderModalOpen(true)}
                className="text-sm text-black hover:underline"
              >
                + Add Header
              </button>
            </div>
            {webhookHeaders.length === 0 ? (
              <p className="text-sm text-gray-500">No custom headers configured.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left">
                      <th className="pb-3 font-medium text-gray-700">Key</th>
                      <th className="pb-3 font-medium text-gray-700">Value</th>
                      <th className="pb-3 font-medium text-gray-700 w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {webhookHeaders.map((header, index) => (
                      <tr key={index} className="hover:bg-gray-50 group">
                        <td className="py-3">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                            {header.key}
                          </code>
                        </td>
                        <td className="py-3 text-gray-600">
                          {maskHeaderValue(header.key, header.value)}
                        </td>
                        <td className="py-3 align-middle">
                          <div className="flex gap-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleDeleteHeader(index)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </SettingsSection>

      <WebhookUrlModal
        isOpen={isUrlModalOpen}
        onClose={handleCloseUrlModal}
        onSave={handleSaveUrl}
        initialUrl={editingUrl}
      />

      <WebhookHeaderModal
        isOpen={isHeaderModalOpen}
        onClose={() => setIsHeaderModalOpen(false)}
        onSave={handleSaveHeader}
      />
    </>
  );
};

const headerKeys = (headers: WebhookHeader[]) =>
  map(headers, ({ key }) => ({ key }));

const normalizeWebhookUris = (
  webhookUri: string | string[] | undefined
): string[] => {
  if (!webhookUri) return [];
  return Array.isArray(webhookUri) ? webhookUri : [webhookUri];
};

const maskHeaderValue = (_key: string, value: string): string => {
  if (value.length > 4) {
    return "••••••••" + value.slice(-4);
  }
  return "••••";
};

export default WebhooksSection;
